import { useEffect } from 'react';
import { NextRouter } from 'next/router';

type UseRedirectToCategoryFallbackProps = {
  router: NextRouter | null;
  // whether the /collections/:slug GetCollection lookup has resolved and failed to find a
  // collection -- avoids redirecting off of a loading/not-yet-known state.
  ready: boolean;
  // the taxonomy category `key` (e.g. 'flower') that the current :slug matches, or null/undefined
  // when it doesn't match a real category either.
  matchedCategoryKey?: string | null;
};

/**
 * `/collections/:slug` (and its embedded-menu/dispensary/stores/kiosks variants) resolves
 * `:slug` via the GetCollection GraphQL query, which only knows about rows in
 * `product_collections`. A slug that matches a real taxonomy category (e.g. the store's only
 * category, "flower") is never a collection row, so GetCollection returns a `Not Found` error --
 * previously surfaced to the shopper as a generic "Server error" page even though the slug is
 * perfectly valid.
 *
 * This mirrors `useCollectionFallbackForProductsRoute` (which lets /products/:category fall
 * back to a collection) in the opposite direction: once the collection lookup has failed and the
 * slug matches a real category, client-side redirect to the sibling /products/:category route --
 * preserving the current app context (dispensary/stores/kiosks/embedded-menu) and query params --
 * so the shopper lands on the real category listing instead of an error page.
 */
export default function useRedirectToCategoryFallback({
  router,
  ready,
  matchedCategoryKey,
}: UseRedirectToCategoryFallbackProps): void {
  useEffect(() => {
    if (!ready || !router || !matchedCategoryKey) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructured only to drop `slug` from the redirected query
    const { slug, ...restQuery } = router.query;

    void router.replace(
      {
        pathname: router.pathname.replace('/collections/[slug]', '/products/[category]'),
        query: { ...restQuery, category: matchedCategoryKey },
      },
      undefined,
      { shallow: false }
    );
  }, [ready, router, matchedCategoryKey]);
}
